import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, forkJoin, of, filter, distinctUntilChanged, skip } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, timeout, catchError, shareReplay, finalize } from 'rxjs/operators';
import { EncryptionService } from '../../services/encryption.service';
import { BrowserStorageService } from '../../services/browser-storage.service';
import { environment } from '../../../environments/environment';
import { BalanceEntity } from '../../domain/models/balance-entity.model';
import { hasSpeiFatherGestor } from '../../domain/models/product-selection.model';

interface ConsultaSaldoResponse {
  success: boolean;
  message: string;
  data: {
    respuesta: {
      cuentaOrdenante: string;
      saldo: number;
    }
  };
}

@Injectable({
  providedIn: 'root'
})
export class AvailableAmountService {

  private readonly BALANCE_VALUE_KEY = 'balance_value'; // <-- CAMBIO: antes se usaba 'balance'
  private readonly BALANCE_OBJECT_KEY = 'balance_object'; // <-- CAMBIO: antes se usaba 'balance'

  /**
   * Timeout de las llamadas de balances. Default 60s (paridad con dev, evita que
   * un endpoint de agregación anidada lento devuelva balances vacíos por timeout).
   * Se puede acortar por entorno con `balanceTimeoutMs` (p. ej. 20000) donde el
   * riesgo de colgar el login pese más que el de un balance vacío.
   */
  private readonly BALANCE_HTTP_TIMEOUT_MS = environment.balanceTimeoutMs ?? 60000;

  // Subjects
  private balanceObjectSubject!: BehaviorSubject<any[]>; // <-- CAMBIO: ahora emite array siempre
  private balanceSubject!: BehaviorSubject<number>;
  private clabeSubject!: BehaviorSubject<string>;
  public balance$!: Observable<number>;
  public balanceInfo$!: Observable<any[]>; // <-- CAMBIO: tipo Observable de array
  public clabe$!: Observable<string>;
  private isBrowser: boolean = false;
  accountType!: any;
  targetEndpoint!: any;

  /** Cache de cuentas padre por polipay_account - una sola llamada API por padre por sesión */
  private fatherAccountCache = new Map<string, any>();

  /** Peticiones crudas EN VUELO a los endpoints de balances por nivel
   *  (company/business_unit/account/father_account con params id_*). Clave = URL
   *  completa (incluye query params). Deduplica SOLO llamadas concurrentes idénticas
   *  —el drill del selector y la tabla de /entities disparan la misma
   *  `?id_company=X` muchas veces a la vez— colapsándolas en un único request. Se
   *  libera al completar (finalize), así que una llamada POSTERIOR va fresca a la red:
   *  no introduce datos viejos (post-transferencia / cambio de nivel siguen frescos). */
  private balancesInFlight = new Map<string, Observable<any>>();
  /** Las escrituras de la sesión pasan por aquí: si no caben, se borra la clave (no queda el valor viejo). */
  private readonly browserStorage = inject(BrowserStorageService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private encryptionService: EncryptionService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // --- Obtener el objeto completo del balance desde la nueva key BALANCE_OBJECT_KEY ---
    const balanceObjFromStorage = this.safeGetBalanceObjectFromStorage(); // <-- CAMBIO: método actualizado
    // initialBalance: busca propiedades conocidas en el primer elemento del array si existe
    // initialBalance: busca propiedades conocidas en el primer elemento del array si existe
    const initialBalance = (Array.isArray(balanceObjFromStorage) && balanceObjFromStorage[0])
      ? this.determineBalance(balanceObjFromStorage[0])
      : 0.00;

    // Inicializar subjects
    this.balanceObjectSubject = new BehaviorSubject<any[]>(balanceObjFromStorage || []);
    this.balanceSubject = new BehaviorSubject<number>(initialBalance);
    this.clabeSubject = new BehaviorSubject<string>((Array.isArray(balanceObjFromStorage) && balanceObjFromStorage[0]?.clabe) ? balanceObjFromStorage[0].clabe : '');

    this.balance$ = this.balanceSubject.asObservable();
    this.clabe$ = this.clabeSubject.asObservable();
    this.balanceInfo$ = this.balanceObjectSubject.asObservable();
  }

  /**
   * Lee el objeto/array de balance desde localStorage (BALANCE_OBJECT_KEY).
   * Retorna un array si existe y es válido, o null.
   */
  private safeGetBalanceObjectFromStorage(): any[] | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;

    const stored = sessionStorage.getItem(this.BALANCE_OBJECT_KEY); // <-- CAMBIO: leer balance_object
    if (!stored) return null;

    let decrypted: string;
    try {
      decrypted = this.encryptionService.decrypt(stored);
    } catch (err) {
      return null;
    }

    try {
      const parsed = JSON.parse(decrypted);
      // Si es array y tiene al menos 1 elemento lo devolvemos
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
      // Si el backend a veces devuelve un objeto en vez de array, lo normalizamos a array
      if (parsed && typeof parsed === 'object') {
        return [parsed];
      }
    } catch (err) {
    }

    return null;
  }

  /**
   * Determina el balance correcto basado en el tipo de entidad (entity_type).
   * Jerarquía: Cta_Padre → Empresa → Unidad de Negocio → Cuenta.
   * Cada nivel usa su campo específico de saldo.
   */
  private determineBalance(info: any): number {
    if (!info) return 0.00;

    const entityType = (info.entity_type ?? '').toString().trim();

    // Por entity_type (jerarquía del árbol)
    switch (entityType) {
      case 'Cta_Padre':
        if (info.balance_father_account != null) return Number(info.balance_father_account);
        break;
      case 'Empresa':
        if (info.balance_company != null) return Number(info.balance_company);
        if (info.balance != null) return Number(info.balance);
        break;
      case 'Unidad de Negocio':
      case 'Unidad  de Negocio':
        if (info.global_balance_unit != null) return Number(info.global_balance_unit);
        if (info.balance != null) return Number(info.balance);
        break;
      case 'Cuenta':
        if (info.balance_account != null) return Number(info.balance_account);
        if (info.balance != null) return Number(info.balance);
        break;
    }

    // Fallback: buscar cualquier campo de saldo conocido
    if (info.balance_account != null) return Number(info.balance_account);
    if (info.balance_father_account != null) return Number(info.balance_father_account);
    if (info.global_balance_unit != null) return Number(info.global_balance_unit);
    if (info.balance_company != null) return Number(info.balance_company);
    if (info.balance != null) return Number(info.balance);

    return 0.00;
  }

  /**
  * Establece el saldo disponible en localStorage (solo valor simple)
  * @param balance - El saldo a establecer
  */
  setBalance(balance: number): void {
    if (this.isBrowser && typeof sessionStorage !== 'undefined') {
      const encryptedBalance = this.encryptionService.encrypt(balance.toString());
      // <-- CAMBIO: ahora guardamos explicitamente en BALANCE_VALUE_KEY (no en 'balance')
      sessionStorage.setItem(this.BALANCE_VALUE_KEY, encryptedBalance);
    }
    this.balanceSubject.next(balance);
  }

  /**
  * Obtiene el saldo disponible desde localStorage (lee BALANCE_OBJECT_KEY y extrae la propiedad conocida)
  * @returns El saldo disponible o 0.00 por defecto
  */
  private safeGetBalanceFromStorage(): number {
    if (!this.isBrowser) return 0.00;

    const raw = sessionStorage.getItem(this.BALANCE_OBJECT_KEY); // <-- CAMBIO: leer balance_object
    if (!raw) return 0.00;

    let decrypted: string;
    try {
      decrypted = this.encryptionService.decrypt(raw);
    } catch {
      return 0.00;
    }

    let arr: any[];
    try {
      const parsed = JSON.parse(decrypted);
      arr = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return 0.00;
    }

    if (!Array.isArray(arr) || arr.length === 0) return 0.00;

    const info = arr[0];
    return this.determineBalance(info);
  }

  /** Key de sessionStorage para el padre seleccionado */
  private readonly SELECTED_FATHER_KEY = 'selectedFatherAccount';

  /**
   * Guarda un padre en el cache para evitar llamadas repetidas a la API.
   */
  cacheFatherAccount(polipayAccount: string, data: any): void {
    if (polipayAccount && data) {
      this.fatherAccountCache.set(polipayAccount, data);
    }
  }

  /**
   * Obtiene un padre desde el cache (sin llamar a la API).
   */
  getCachedFatherAccount(polipayAccount: string): any | null {
    return polipayAccount ? this.fatherAccountCache.get(polipayAccount) ?? null : null;
  }

  /**
   * Obtiene el padre actual desde selectedFatherAccount (sessionStorage).
   */
  getSelectedFatherAccount(): any | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const stored = sessionStorage.getItem(this.SELECTED_FATHER_KEY);
    if (!stored) return null;
    try {
      const decrypted = this.encryptionService.decrypt(stored);
      const parsed = JSON.parse(decrypted);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      return arr?.[0] ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Busca en father_accounts_list (sessionStorage) la Cta_Padre con la MISMA
   * identidad canónica (provider + id_entity_name) que `entity`. Fuente completa
   * cargada al login, con el árbol de companies de cada padre — útil cuando el
   * refetch por polipay compartido no puede desambiguar al padre correcto.
   */
  findFatherInSessionList(entity: any): any | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const stored = sessionStorage.getItem('father_accounts_list');
    if (!stored) return null;
    try {
      const list = JSON.parse(this.encryptionService.decrypt(stored));
      if (!Array.isArray(list)) return null;
      return list.find((f: any) => this.isSameEntityIdentity(f, entity)) ?? null;
    } catch {
      return null;
    }
  }

  getSelectedCompany(): any | null {
    return this.readEncryptedSession('selectedCompany');
  }

  getSelectedBusinessUnit(): any | null {
    return this.readEncryptedSession('selectedBusinessUnit');
  }

  getSelectedAccount(): any | null {
    return this.readEncryptedSession('selectedAccount');
  }

  getCurrentBalanceEntity(): any | null {
    return this.readEncryptedSession('balance_object');
  }

  /**
   * La cuenta PADRE del contexto activo. UNA definición para toda la suite.
   *
   * Por qué existe: tres componentes (el menú lateral, el saldo y el breadcrumb)
   * tenían esta resolución COPIADA y los tres tomaban
   * `selectedFatherAccount[0]`. Ese `[0]` NO es el padre activo: `entities`
   * guarda ahí `[...asignacionesDirectas, ...padres]` y el orden es deliberado
   * —las directas van primero para preservar el `entities[0]` del que depende
   * `setBalanceInfo`—, así que la primera posición suele ser una empresa o unidad
   * asignada directamente, de CUALQUIER proveedor del usuario.
   *
   * De ahí el síntoma: navegando dentro de Transfer y volviendo al nivel raíz, el
   * menú mostraba STP, el saldo podía resolver otro padre distinto y la
   * información se leía superpuesta — cada consumidor resolvía por su cuenta y
   * podían discrepar entre sí.
   *
   * Se resuelve por IDENTIDAD y no por posición:
   *   1. si la entidad activa YA es una cuenta padre, es ésa;
   *   2. si no, se toma su PROVEEDOR (las entidades guardadas lo arrastran) y se
   *      busca la cuenta padre de ese proveedor;
   *   3. como último recurso, la primera cuenta padre que exista — nunca un
   *      elemento cualquiera de la lista.
   */
  getActiveFatherAccount(): BalanceEntity | null {
    const activa: BalanceEntity | null = this.readEncryptedSession('balance_object');
    if (this.esCuentaPadre(activa)) return activa;

    const proveedor = (activa?.provider ?? '').toString().trim();
    return this.buscarCuentaPadre('selectedFatherAccount', proveedor)
      ?? this.buscarCuentaPadre('father_accounts_list', proveedor);
  }

  private esCuentaPadre(e: BalanceEntity | null | undefined): boolean {
    return !!e && (e.entity_type ?? '').toString().trim() === 'Cta_Padre';
  }

  /**
   * Cuenta padre de `proveedor` dentro de la llave dada; si no hay coincidencia,
   * la primera cuenta padre de la lista.
   *
   * El respaldo NUNCA es `lista[0]` a secas: ése es el elemento que le sirve a
   * `setBalanceInfo`, no necesariamente una cuenta padre.
   */
  private buscarCuentaPadre(llave: string, proveedor: string): BalanceEntity | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const guardado = sessionStorage.getItem(llave);
    if (!guardado) return null;
    try {
      const parsed = JSON.parse(this.encryptionService.decrypt(guardado));
      const lista: BalanceEntity[] = Array.isArray(parsed) ? parsed : [parsed];
      const padres = lista.filter((e) => this.esCuentaPadre(e));
      if (!padres.length) return null;
      if (proveedor) {
        const propio = padres.find(
          (e) => (e?.provider ?? '').toString().trim() === proveedor,
        );
        if (propio) return propio;
      }
      return padres[0];
    } catch {
      return null;
    }
  }

  /**
   * Emite la entidad activa cada vez que cambia DE VERDAD (ignora la carga inicial y
   * los re-emits de la misma entidad). Pensado para que cada pantalla recargue sus datos
   * al cambiar de nivel en el breadcrumb, SIN navegar. Como setBalanceInfo ahora persiste
   * en sessionStorage ANTES de emitir, al reaccionar el balance_object ya tiene la entidad
   * nueva — así cada servicio que lee el clabe del storage obtiene el correcto.
   */
  entityChanges(): Observable<any> {
    return this.balanceInfo$.pipe(
      map((info: any) => Array.isArray(info) ? info[0] : info),
      filter((e: any) => !!e),
      distinctUntilChanged((a: any, b: any) => this.entityIdentity(a) === this.entityIdentity(b)),
      skip(1)
    );
  }

  /** Identidad estable de una entidad para detectar cambios reales. */
  private entityIdentity(e: any): string {
    return ['entity_type', 'clabe', 'clabe_account', 'polipay_account', 'id_account', 'id_business_unit', 'id_company']
      .map(k => (e?.[k] ?? '').toString().trim()).join('|');
  }

  private readEncryptedSession(key: string): any | null {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return null;
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    try {
      const decrypted = this.encryptionService.decrypt(stored);
      const parsed = JSON.parse(decrypted);
      return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      return null;
    }
  }

  /**
   * Obtiene la empresa con business_units desde el árbol del padre actual.
   * Evita mezclar datos de otro proveedor/cuenta padre.
   */
  getCompanyFromParentContext(companyPolipayOrId: string): Observable<any> {
    const parent = this.getSelectedFatherAccount();
    const parentPolipay = parent?.polipay_account || parent?.clabe;
    if (!parentPolipay) return of(null);

    const findInParent = (p: any) => {
      const companies = p?.companies ?? [];
      return companies.find((c: any) =>
        c?.polipay_account === companyPolipayOrId ||
        c?.clabe === companyPolipayOrId ||
        c?.id_company === companyPolipayOrId
      ) ?? null;
    };

    let company = findInParent(parent);
    if (company?.business_units?.length) return of(company);

    const cached = this.getCachedFatherAccount(parentPolipay);
    if (cached) {
      company = findInParent(cached);
      if (company?.business_units?.length) return of(company);
    }

    // Con carga progresiva del backend, BUs no vienen sin ?id_company.
    // Usar el id_company para pedir la empresa con sus BUs expandidas.
    const idCompany = company?.id_company ?? parent?.companies?.find(
      (c: any) => c?.polipay_account === companyPolipayOrId || c?.clabe === companyPolipayOrId
    )?.id_company;

    if (idCompany && parentPolipay) {
      return this.searchSubEntities(parentPolipay, { id_company: idCompany }).pipe(
        map((items) => {
          for (const item of items) {
            if (item?.companies?.length) {
              const found = item.companies.find((c: any) => String(c.id_company) === String(idCompany));
              if (found) return found;
            }
            if (String(item?.id_company) === String(idCompany) || item?.business_units?.length) {
              return item;
            }
          }
          return company ?? null;
        })
      );
    }

    return this.getFatherAccountByPolipay(parentPolipay).pipe(
      map((father) => findInParent(father) ?? null)
    );
  }

  /**
   * Obtiene la unidad de negocio con accounts desde el árbol del padre actual.
   */
  getBusinessUnitFromParentContext(buPolipayOrId: string): Observable<any> {
    const parent = this.getSelectedFatherAccount();
    const parentPolipay = parent?.polipay_account || parent?.clabe;
    if (!parentPolipay) return of(null);

    const findInParent = (p: any) => {
      for (const c of p?.companies ?? []) {
        for (const bu of c?.business_units ?? []) {
          if (bu?.polipay_account === buPolipayOrId || bu?.clabe === buPolipayOrId ||
              bu?.id_business_unit === buPolipayOrId || bu?.clabe_account === buPolipayOrId) {
            return bu;
          }
        }
      }
      return null;
    };

    let bu = findInParent(parent);
    if (bu?.accounts?.length) return of(bu);

    const cached = this.getCachedFatherAccount(parentPolipay);
    if (cached) {
      bu = findInParent(cached);
      if (bu?.accounts?.length) return of(bu);
    }

    // Con carga progresiva del backend, accounts no vienen sin ?id_business_unit.
    // Buscar el id_company y id_business_unit para pedir la BU con sus accounts expandidas.
    const buRef = bu ?? parent?.companies?.flatMap(
      (c: any) => (c?.business_units ?? []).map((b: any) => ({ ...b, id_company: c.id_company }))
    )?.find((b: any) =>
      b?.polipay_account === buPolipayOrId || b?.clabe === buPolipayOrId ||
      b?.id_business_unit === buPolipayOrId || b?.clabe_account === buPolipayOrId
    );
    const idCompany = buRef?.id_company;
    const idBU = buRef?.id_business_unit ?? buPolipayOrId;

    if (idBU && parentPolipay) {
      const filter: any = { id_business_unit: idBU };
      if (idCompany) filter.id_company = idCompany;
      return this.searchSubEntities(parentPolipay, filter).pipe(
        map((items) => {
          for (const item of items) {
            if (item?.companies?.length) {
              for (const c of item.companies) {
                const found = (c.business_units ?? []).find((b: any) => String(b.id_business_unit) === String(idBU));
                if (found) return found;
              }
            }
            if (item?.business_units?.length) {
              const found = item.business_units.find((b: any) => String(b.id_business_unit) === String(idBU));
              if (found) return found;
            }
            if (String(item?.id_business_unit) === String(idBU) && item?.accounts?.length) {
              return item;
            }
          }
          return bu ?? null;
        })
      );
    }

    return this.getFatherAccountByPolipay(parentPolipay).pipe(
      map((father) => findInParent(father) ?? null)
    );
  }

  /**
   * Obtiene la estructura de una cuenta padre (Cta_Padre) por su polipay_account.
   * Usa cache si está disponible; si no, llama a la API una sola vez y cachea.
   * No actualiza sessionStorage ni subjects.
   * @param forceRefresh ignora el cache y siempre llama a la API (usar tras
   *                     transferencias o al entrar a vistas que muestran saldos)
   */
  getFatherAccountByPolipay(polipayAccount: string, forceRefresh = false): Observable<any> {
    if (!polipayAccount) return of(null);

    if (!forceRefresh) {
      const cached = this.getCachedFatherAccount(polipayAccount);
      if (cached) return of(cached);
    } else {
      this.fatherAccountCache.delete(polipayAccount);
    }

    // Compartimos el fetch crudo del árbol del padre con TODOS los consumidores del mismo
    // endpoint (getFatherAccountsList, refreshAllBalancesFromAPI y otros
    // getFatherAccountByPolipay) vía el mapa único balancesInFlight. Al entrar a una
    // Cta_Padre estas llamadas concurrentes idénticas colapsan en UN solo request en vez
    // de pegar el endpoint pesado 3-4 veces (causa del ~10s). La selección del padre (map)
    // corre por consumidor, así que cada uno resuelve SU padre según su balance_object —
    // no altera datos ni lo que se ve.
    const token = sessionStorage.getItem('access');
    const endpoint = `${environment.apiUrl}/api/father_account/balances/${polipayAccount}/`;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.dedupedBalancesGet(endpoint, headers, new HttpParams()).pipe(
      map((response) => {
        if (!Array.isArray(response) || response.length === 0) return null;

        // BLINDAJE anti-fuga cross-provider: con VARIOS padres NO usamos response[0] por
        // defecto (STP), porque sería mostrar el proveedor equivocado. `father` queda null
        // hasta que un match CONFIABLE (clabe / nombre+provider / nombre / polipay) lo
        // identifique; si nada matchea se devuelve null y el caller cae al contexto
        // cacheado correcto o a vacío — nunca a otro proveedor.
        let father: any = response.length === 1 ? response[0] : null;
        if (response.length > 1) {
          // Cuando dos Cta_Padre comparten polipay_account (sufijo compartido entre
          // providers, p.ej. STP + ASP), un match por polipay_account devuelve el
          // primer elemento del array sin verificar que sea el padre seleccionado.
          // Se desambigua SIEMPRE primero por identidad de balance_object
          // (id_entity_name + provider, o clabe), que refleja la selección real
          // del usuario en el carrusel. Sólo si no hay balance_object o no hay
          // coincidencia por identidad se cae al match por el parámetro.
          const currentBo = this.safeGetBalanceObjectFromStorage();
          const currentFirst = Array.isArray(currentBo) && currentBo[0] ? currentBo[0] : currentBo;
          const currentClabe = (currentFirst?.clabe ?? '').toString().trim();
          const currentName = (currentFirst?.id_entity_name ?? '').toString().trim();
          const currentProvider = (currentFirst?.provider ?? '').toString().trim();

          let resolved: any = null;
          // Identidad canónica de Cta_Padre PRIMERO: (id_entity_name, provider) —
          // misma regla que matchEntityInResponse. La clabe va después porque
          // puede compartirse entre padres de providers distintos, y matchearla
          // primero devolvía el primer padre del response con esa clabe
          // (proveedor equivocado o, si no coincidía nada, lista vacía).
          if (currentName && currentProvider) {
            resolved = response.find((r: any) =>
              (r?.id_entity_name ?? '').toString().trim() === currentName &&
              (r?.provider ?? '').toString().trim().toUpperCase() === currentProvider.toUpperCase()
            );
          }
          if (!resolved && currentName) {
            resolved = response.find((r: any) =>
              (r?.id_entity_name ?? '').toString().trim() === currentName
            );
          }
          if (!resolved && currentClabe) {
            resolved = response.find((r: any) => String(r?.clabe ?? '').trim() === currentClabe);
          }
          if (!resolved) {
            // Fallback: match por el parámetro polipayAccount. Última opción porque,
            // si el parámetro es un sufijo compartido, todos los padres lo cumplen
            // y find devuelve response[0] (no necesariamente el correcto).
            resolved = response.find((r: any) =>
              String(r?.clabe ?? '') === String(polipayAccount) ||
              String(r?.polipay_account ?? '') === String(polipayAccount)
            );
          }
          if (resolved) father = resolved;
        }

        if (father) this.cacheFatherAccount(polipayAccount, father);
        return father;
      }),
      // Preserva el comportamiento previo (antes el catchError iba en el fetch crudo):
      // ante error del endpoint, resolver a null en vez de propagar.
      catchError(() => of(null))
    );
  }

  /**
   * Obtiene el listado de cuentas padre (Cta_Padre) para el usuario.
   * El API puede devolver un array directamente o un objeto único.
   * Soporta respuesta: [{parent1},{parent2}], [singleParent], {data:[...]}, o single object.
   */
  getFatherAccountsList(polipayAccount: string): Observable<any[]> {
    if (!polipayAccount) return of([]);

    const token = sessionStorage.getItem('access');
    const endpoint = `${environment.apiUrl}/api/father_account/balances/${polipayAccount}/`;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    return this.http.get<any>(endpoint, { headers }).pipe(
      timeout(this.BALANCE_HTTP_TIMEOUT_MS),
      map((response) => {
        let parents: any[] = [];
        if (Array.isArray(response)) {
          parents = response.filter((r: any) => r?.entity_type === 'Cta_Padre');
        } else if (Array.isArray(response?.data)) {
          parents = response.data.filter((r: any) => r?.entity_type === 'Cta_Padre');
        } else if (response?.entity_type === 'Cta_Padre') {
          parents = [response];
        } else {
          const first = response?.[0];
          if (first?.entity_type === 'Cta_Padre') parents = [first];
        }
        parents.forEach((p: any) => {
          const key = p?.polipay_account || p?.clabe;
          if (key) this.cacheFatherAccount(key, p);
        });
        return parents;
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }

  /**
   * Obtiene las entidades desde el endpoint correspondiente al entity_type del gestor.
   * Mapea los 4 niveles de jerarquía a su endpoint:
   *   Cta_Padre → /api/father_account/balances/
   *   Empresa → /api/company/balances/
   *   Unidad de Negocio → /api/business_unit/balances/
   *   Cuenta → /api/account/balances/
   * Retorna el array de entidades del nivel indicado (con subentidades anidadas).
   */
  /**
   * GET crudo a un endpoint de balances con deduplicación de peticiones concurrentes
   * idénticas. La clave es la URL completa (endpoint + query params), de modo que dos
   * llamadas simultáneas al mismo nivel/entidad comparten un solo request HTTP. Al
   * completar se libera la entrada (finalize) para que la siguiente llamada vaya fresca.
   * Devuelve la respuesta cruda; el mapeo/errores quedan por-suscriptor (comportamiento
   * idéntico al de hoy, solo se elimina el request duplicado).
   */
  private dedupedBalancesGet(url: string, headers: HttpHeaders, params: HttpParams): Observable<any> {
    const key = `${url}?${params.toString()}`;
    let raw$ = this.balancesInFlight.get(key);
    if (!raw$) {
      raw$ = this.http.get<any>(url, { headers, params }).pipe(
        timeout(60000),
        finalize(() => this.balancesInFlight.delete(key)),
        shareReplay(1)
      );
      this.balancesInFlight.set(key, raw$);
    }
    return raw$;
  }

  getAccountsByEntityType(
    entityType: string,
    polipayAccount: string,
    filter?: { id_company?: string | number; id_business_unit?: string | number; id_account?: string | number }
  ): Observable<any[]> {
    if (!polipayAccount) return of([]);

    const endpointMap: Record<string, string> = {
      'Cta_Padre': 'father_account',
      'Empresa': 'company',
      'Unidad de Negocio': 'business_unit',
      'Unidad  de Negocio': 'business_unit',
      'Cuenta': 'account',
    };

    const endpoint = endpointMap[entityType.trim()];
    if (!endpoint) return of([]);

    const token = sessionStorage.getItem('access');
    const url = `${environment.apiUrl}/api/${endpoint}/balances/${polipayAccount}/`;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    let params = new HttpParams();
    if (filter?.id_company != null) params = params.set('id_company', String(filter.id_company));
    if (filter?.id_business_unit != null) params = params.set('id_business_unit', String(filter.id_business_unit));
    if (filter?.id_account != null) params = params.set('id_account', String(filter.id_account));

    return this.dedupedBalancesGet(url, headers, params).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (response && typeof response === 'object') return [response];
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * FUENTE ÚNICA DE VERDAD para los hijos de una entidad dentro del `company-selector`.
   * Usa el MISMO endpoint por id que la tabla de /entities (`getAccountsByEntityType`),
   * de modo que lo que se ve DENTRO del selector === lo que se ve en la tabla.
   * La extracción espeja el descenso probado de `mapSearchResultToAccounts`
   * (entities.component): la respuesta puede venir como Cta_Padre→companies→business_units
   * →accounts, como Empresa→business_units, como BU→accounts o como lista directa.
   *
   * @param entity      entidad actual (companyInfo) — debe traer entity_type y los ids
   * @param rootPolipay polipay_account de la Cta_Padre raíz (getRootPolipay del selector)
   */
  getChildrenForLevel(entity: any, rootPolipay: string): Observable<any[]> {
    if (!entity) return of([]);
    const norm = (entity.entity_type ?? '').toString().trim().replace(/\s+/g, ' ');

    // Cta_Padre -> Empresas
    if (norm === 'Cta_Padre') {
      // Empresas desde el árbol EN MANO: father_accounts_list (identidad canónica
      // provider+nombre) o la propia entidad. Es el respaldo cuando el refetch no
      // aplica o no puede desambiguar al padre correcto.
      const companiesInHand = (): any[] => {
        const source = this.findFatherInSessionList(entity) ?? entity;
        return Array.isArray(source?.companies)
          ? source.companies.map((c: any) => ({ ...c, entity_type: 'Empresa' }))
          : [];
      };

      // Hay padres SIN polipay_account ni clabe (el serializer del backend
      // devuelve polipay_account null cuando el gestor no está encadenado):
      // sin raíz no hay refetch posible — se listan las empresas en mano.
      if (!rootPolipay) return of(companiesInHand());

      // Varias Cta_Padre pueden COMPARTIR polipay_account (p.ej. STP y TRANSFER). Un fetch
      // directo por polipay a /company/balances devolvería las empresas del primer padre
      // (STP) sin importar en cuál estás. Usamos la MISMA resolución desambiguada que /entities
      // (getFatherAccountByPolipay, que casa por identidad contra balance_object) y con
      // forceRefresh para no leer el caché contaminado por el polipay compartido.
      return this.getFatherAccountByPolipay(rootPolipay, true).pipe(
        map((father: any) => {
          // El response del polipay COMPARTIDO puede resolver OTRO padre (incluso
          // sin pasar por la desambiguación: con response de un solo elemento se
          // acepta directo). Solo se usa si es EL MISMO padre por identidad
          // canónica (provider + nombre); si no, empresas en mano.
          const sameFather = !!father && this.isSameEntityIdentity(father, entity);
          if (sameFather && Array.isArray(father?.companies) && father.companies.length > 0) {
            return father.companies.map((c: any) => ({ ...c, entity_type: 'Empresa' }));
          }
          return companiesInHand();
        })
      );
    }

    // Los niveles inferiores sí exigen la raíz: sus cargas van por API con el polipay padre.
    if (!rootPolipay) return of([]);

    // Empresa -> Unidades de Negocio
    if (norm === 'Empresa') {
      const idCompany = entity.id_company;
      return this.getAccountsByEntityType('Empresa', rootPolipay, { id_company: idCompany }).pipe(
        map((items) => this.extractBusinessUnits(items, idCompany).map((b: any) => ({
          ...b,
          entity_type: 'Unidad de Negocio',
          id_company: b.id_company ?? idCompany,
        })))
      );
    }

    // Unidad de Negocio -> Cuentas
    if (norm === 'Unidad de Negocio') {
      const idCompany = entity.id_company;
      const idBU = entity.id_business_unit;
      return this.getAccountsByEntityType('Unidad de Negocio', rootPolipay, {
        id_company: idCompany,
        id_business_unit: idBU,
      }).pipe(
        map((items) => this.extractAccounts(items, idBU).map((a: any) => ({
          ...a,
          entity_type: 'Cuenta',
          clabe: a.clabe ?? a.clabe_account,
          id_company: a.id_company ?? idCompany,
          id_business_unit: a.id_business_unit ?? idBU,
        })))
      );
    }

    return of([]); // Cuenta = hoja, sin hijos
  }

  /** Extrae las empresas de la respuesta (Cta_Padre.companies o lista directa de empresas). */
  private extractCompanies(items: any[]): any[] {
    if (!Array.isArray(items)) return [];
    const out: any[] = [];
    for (const item of items) {
      const et = (item?.entity_type ?? '').toString().trim();
      if (et === 'Cta_Padre' || (item?.companies?.length && !item?.business_units && !item?.accounts)) {
        for (const c of item.companies ?? []) out.push(c);
      } else if (et === 'Empresa' || item?.id_company != null) {
        out.push(item);
      }
    }
    return out;
  }

  /** Extrae las unidades de negocio de la empresa `idCompany` (tolerante a la forma de la respuesta). */
  private extractBusinessUnits(items: any[], idCompany: any): any[] {
    if (!Array.isArray(items)) return [];
    const matchCompany = (c: any) => idCompany == null || String(c?.id_company) === String(idCompany);
    const out: any[] = [];
    for (const item of items) {
      const et = (item?.entity_type ?? '').toString().trim();
      if (et === 'Cta_Padre' || (item?.companies?.length && !item?.business_units && !item?.accounts)) {
        for (const c of item.companies ?? []) {
          if (matchCompany(c)) for (const bu of c.business_units ?? []) out.push({ ...bu, id_company: c.id_company });
        }
      } else if (et === 'Empresa' || item?.business_units?.length) {
        if (matchCompany(item)) for (const bu of item.business_units ?? []) out.push({ ...bu, id_company: item.id_company });
      } else if (et === 'Unidad de Negocio' || et === 'Unidad  de Negocio' || item?.id_business_unit != null) {
        out.push(item); // la respuesta YA es lista de UN
      }
    }
    return out;
  }

  /** Extrae las cuentas de la unidad de negocio `idBU` (tolerante a la forma de la respuesta). */
  private extractAccounts(items: any[], idBU: any): any[] {
    if (!Array.isArray(items)) return [];
    const matchBU = (bu: any) => idBU == null || String(bu?.id_business_unit) === String(idBU);
    const out: any[] = [];
    const pushFromBU = (bu: any) => {
      for (const a of bu?.accounts ?? []) out.push({ ...a, id_business_unit: bu.id_business_unit, id_company: bu.id_company });
    };
    for (const item of items) {
      const et = (item?.entity_type ?? '').toString().trim();
      if (et === 'Cta_Padre' || (item?.companies?.length && !item?.business_units && !item?.accounts)) {
        for (const c of item.companies ?? []) {
          for (const bu of c.business_units ?? []) if (matchBU(bu)) pushFromBU({ ...bu, id_company: c.id_company });
        }
      } else if (et === 'Empresa' || (item?.business_units?.length && !item?.accounts)) {
        for (const bu of item.business_units ?? []) if (matchBU(bu)) pushFromBU({ ...bu, id_company: item.id_company });
      } else if (et === 'Unidad de Negocio' || et === 'Unidad  de Negocio' || item?.accounts?.length) {
        if (matchBU(item)) pushFromBU(item);
      } else if (et === 'Cuenta' || item?.id_account != null || item?.clabe_account != null) {
        out.push(item); // la respuesta YA es lista de cuentas
      }
    }
    return out;
  }

  /**
   * Obtiene en paralelo los conteos de Empresa, Unidad de Negocio y Cuenta
   * para una Cta_Padre. forkJoin dispara las 3 peticiones a la vez y emite
   * cuando las 3 completan. Errores individuales no propagan: getAccountsByEntityType
   * tiene catchError que devuelve [] si el HTTP falla, así que el conteo de ese
   * nivel queda en 0 pero el resto sí llega.
   */
  getEntityCountsForParent(polipayAccount: string): Observable<{ companies: number; businessUnits: number; accounts: number }> {
    if (!polipayAccount) {
      return of({ companies: 0, businessUnits: 0, accounts: 0 });
    }
    return forkJoin({
      companies: this.getAccountsByEntityType('Empresa', polipayAccount),
      businessUnits: this.getAccountsByEntityType('Unidad de Negocio', polipayAccount),
      accounts: this.getAccountsByEntityType('Cuenta', polipayAccount),
    }).pipe(
      map(({ companies, businessUnits, accounts }) => ({
        companies: companies.length,
        businessUnits: businessUnits.length,
        accounts: accounts.length,
      }))
    );
  }

  /**
   * Busca sub-entidades de una cuenta padre filtrando por nivel jerárquico.
   * Endpoint: GET /api/father_account/balances/{polipayAccount}?id_company=X
   *                                                             &id_business_unit=Y
   *                                                             &id_account=Z
   * @param polipayAccount - polipay_account de la Cta_Padre raíz
   * @param filter - filtros opcionales por id_company, id_business_unit o id_account
   */
  searchSubEntities(
    polipayAccount: string,
    filter: {
      id_company?: string | number;
      id_business_unit?: string | number;
      id_account?: string | number;
    }
  ): Observable<any[]> {
    if (!polipayAccount) return of([]);

    const token = sessionStorage.getItem('access');
    const url = `${environment.apiUrl}/api/father_account/balances/${polipayAccount}/`;

    let headers = new HttpHeaders();
    if (token) headers = headers.set('Authorization', 'Bearer ' + token);

    let params = new HttpParams();
    if (filter.id_company != null)       params = params.set('id_company',       String(filter.id_company));
    if (filter.id_business_unit != null) params = params.set('id_business_unit', String(filter.id_business_unit));
    if (filter.id_account != null)       params = params.set('id_account',       String(filter.id_account));

    return this.dedupedBalancesGet(url, headers, params).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (response && typeof response === 'object') return [response];
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Obtiene el saldo de la entidad actualmente seleccionada (balance_object).
   *
   * El backend puede devolver múltiples entidades cuando varias comparten el
   * identificador de consulta (p. ej. dos Cta_Padre con el mismo polipay_account).
   * Este método aplica matching estricto y retorna SOLO la entidad que corresponde
   * a la selección del usuario, o `null` si no hay match seguro.
   *
   * @returns Observable que emite la entidad matcheada o null. Nunca emite `response[0]`
   *          como fallback — para fintech, mejor saldo viejo que saldo de otra cuenta.
   */
  getCurrentBalance(clabe?: string): Observable<BalanceEntity | null> {
    let accountToQuery = clabe;
    // `localAccountType` se captura por-llamada para que dos `getCurrentBalance` concurrentes
    // (típicamente el de la entidad recién seleccionada + uno en vuelo de la anterior) no
    // colisionen sobre `this.accountType` y terminen matcheando la respuesta vieja contra
    // la nueva entidad.
    let localAccountType: any = null;
    if (accountToQuery) {
      const userLocalStorage = sessionStorage.getItem('user');
      const selectedActiveAccount = sessionStorage.getItem('balance_object');

      if (selectedActiveAccount) {
        try {
          const parsedSelectedAccount = JSON.parse(this.encryptionService.decrypt(selectedActiveAccount));
          localAccountType = Array.isArray(parsedSelectedAccount) ? parsedSelectedAccount[0] : parsedSelectedAccount;
        } catch {
          /* fallback a user */
        }
      } else {
        if (userLocalStorage) {
          try {
            localAccountType = JSON.parse(this.encryptionService.decrypt(userLocalStorage));
          } catch {
            /* sin accountType */
          }
        }
      }
    }
    // Espejear en instancia por compatibilidad con consumidores externos; lo que ESTA
    // llamada use viene de `localAccountType` y no se ve afectado por otra concurrente.
    if (localAccountType) this.accountType = localAccountType;

    const accEntityType = (localAccountType?.entity_type ?? '').toString().trim();
    let localTargetEndpoint: string | undefined;
    switch (accEntityType) {
      case 'Cta_Padre':
        localTargetEndpoint = "father_account";
        break;
      case 'Empresa':
        localTargetEndpoint = "company";
        break;
      case 'Unidad de Negocio':
      case 'Unidad  de Negocio':
        localTargetEndpoint = "business_unit";
        break;
      case 'Cuenta':
        localTargetEndpoint = "account";
        break;
      default:
        localTargetEndpoint = undefined;
        break;
    }
    this.targetEndpoint = localTargetEndpoint;

    const token = sessionStorage.getItem('access');
    const url = localTargetEndpoint ? `${environment.apiUrl}/api/${localTargetEndpoint}/balances/${accountToQuery}` : `${environment.apiUrl}/api/father_account/balances/${accountToQuery}`;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    // Construir query params desde la entidad CAPTURADA al inicio (no `this.accountType`)
    // para no leer un nivel que ya cambió por otra selección del usuario.
    let params = new HttpParams();
    if (localAccountType?.id_company) {
      params = params.set('id_company', localAccountType.id_company);
    }
    if ((accEntityType === 'Unidad de Negocio' || accEntityType === 'Unidad  de Negocio' || accEntityType === 'Cuenta') && localAccountType?.id_business_unit) {
      params = params.set('id_business_unit', localAccountType.id_business_unit);
    }
    if (accEntityType === 'Cuenta' && localAccountType?.id_account) {
      params = params.set('id_account', localAccountType.id_account);
    }

    return this.http.get<any>(url, { headers, params }).pipe(
      timeout(this.BALANCE_HTTP_TIMEOUT_MS),
      map(response => {
        const items: any[] = Array.isArray(response)
          ? response
          : (response ? [response] : []);
        if (items.length === 0 || !localAccountType) return null;

        const matched = this.matchEntityInResponse(items, localAccountType);
        if (!matched) return null;

        // Guard estricto contra respuesta stale: si la entidad activa cambió entre el
        // disparo y la respuesta de la API, descartar para no escribir saldos de otra
        // cuenta sobre la selección actual del usuario.
        const currentNow = this.safeGetBalanceObjectFromStorage();
        const currentNowFirst = Array.isArray(currentNow) && currentNow[0] ? currentNow[0] : currentNow;
        if (currentNowFirst && !this.isSameEntityIdentity(currentNowFirst, localAccountType)) {
          return null;
        }

        const respEntityType = (matched?.entity_type ?? localAccountType?.entity_type ?? '').toString().trim();
        const isCuentaContext = respEntityType === 'Cuenta' || accEntityType === 'Cuenta';
        const newBalance = this.determineBalance(matched);
        let balanceToSet = newBalance;
        if (isCuentaContext && newBalance === 0) {
          const cached = this.safeGetBalanceObjectFromStorage();
          const cachedFirst = Array.isArray(cached) && cached[0] ? cached[0] : cached;
          if (cachedFirst?.balance_account != null) {
            balanceToSet = Number(cachedFirst.balance_account);
          }
        }
        this.setBalance(balanceToSet);
        this.clabeSubject.next(matched?.clabe ?? '');
        return matched;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Compara si dos objetos representan la MISMA entidad seleccionada por el usuario.
   * Igualdad estricta por `entity_type` + el id propio de su nivel (`id_account` /
   * `id_business_unit` / `id_company`) o `clabe`. Para Cta_Padre usa `(provider, id_entity_name)`.
   * Si la nueva carece de identidad, asumir match (no descartar — datos legacy).
   */
  private isSameEntityIdentity(a: any, b: any): boolean {
    if (!a || !b) return false;
    const trim = (v: any) => (v ?? '').toString().trim();
    const aType = trim(a.entity_type);
    const bType = trim(b.entity_type);
    if (aType && bType && aType !== bType) return false;

    if (aType === 'Cta_Padre' || bType === 'Cta_Padre') {
      const aKey = `${trim(a.provider)}::${trim(a.id_entity_name)}`;
      const bKey = `${trim(b.provider)}::${trim(b.id_entity_name)}`;
      return aKey === bKey && aKey !== '::';
    }

    const eq = (x: any, y: any) => x != null && y != null && String(x) === String(y);
    if (eq(a.id_account, b.id_account)) return true;
    if (eq(a.id_business_unit, b.id_business_unit) && (aType === 'Unidad de Negocio' || aType === 'Unidad  de Negocio')) return true;
    if (eq(a.id_company, b.id_company) && aType === 'Empresa') return true;
    if (eq(a.clabe, b.clabe) && trim(a.clabe)) return true;
    return false;
  }

  /**
   * Selecciona del response de la API la entidad que corresponde estrictamente a `current`.
   *
   * Reglas de matching, en orden de prioridad:
   * - Empresa/UN/Cuenta: por id específico (`id_company`/`id_business_unit`/`id_account`)
   *   o por `clabe` cuando el id no está disponible.
   * - Cta_Padre: por identidad canónica `(provider, id_entity_name)`. `clabe` no existe
   *   a nivel padre y `polipay_account`/`sufijo` pueden colisionar entre padres del mismo
   *   usuario, por lo que NO se usan como discriminador.
   *
   * Retorna `null` si no hay match estricto. No cae al primer item del response — para
   * fintech, devolver una entidad equivocada como propia es un riesgo regulatorio mayor
   * que devolver datos desactualizados.
   */
  private matchEntityInResponse(items: any[], current: any): any | null {
    if (!Array.isArray(items) || items.length === 0 || !current) return null;
    const entityType = (current?.entity_type ?? '').toString().trim();
    const trim = (v: any) => (v ?? '').toString().trim();

    if (entityType === 'Cta_Padre') {
      const name = trim(current?.id_entity_name);
      const provider = trim(current?.provider);
      if (!name || !provider) return null; // sin identidad canónica completa, no aplicar
      return items.find((r: any) =>
        trim(r?.id_entity_name) === name && trim(r?.provider) === provider
      ) ?? null;
    }

    const currentId = current?.id_company ?? current?.id_business_unit ?? current?.id_account;
    const currentClabe = trim(current?.clabe);

    if (items.length === 1 && !currentId && !currentClabe) {
      // Endpoint específico (Empresa/UN/Cuenta) que devuelve un único objeto:
      // confiar en el endpoint cuando no hay discriminador disponible.
      return items[0];
    }

    return items.find((r: any) => {
      const respId = r?.id_company ?? r?.id_business_unit ?? r?.id_account;
      if (currentId && respId && String(currentId) === String(respId)) return true;
      if (currentClabe && trim(r?.clabe) === currentClabe) return true;
      return false;
    }) ?? null;
  }

  /**
   * Obtiene el identificador del usuario (polipay_account, clabe o sufijo) para llamar a la API.
   */
  private getUserPolipayForApi(): string | null {
    const stored = sessionStorage.getItem('user');
    if (stored) {
      try {
        const parsed = JSON.parse(this.encryptionService.decrypt(stored));
        return parsed?.polipay_account || parsed?.clabe || parsed?.sufijo || null;
      } catch {
        /* fallback */
      }
    }
    const balanceObj = this.safeGetBalanceObjectFromStorage();
    const first = Array.isArray(balanceObj) && balanceObj[0] ? balanceObj[0] : balanceObj;
    if (first?.sufijo) return String(first.sufijo);
    const fatherEnc = sessionStorage.getItem('father_accounts_list');
    if (fatherEnc) {
      try {
        const dec = this.encryptionService.decrypt(fatherEnc);
        const fathers = JSON.parse(dec);
        const fa = Array.isArray(fathers) && fathers[0] ? fathers[0] : fathers;
        return fa?.sufijo || fa?.polipay_account || fa?.clabe || null;
      } catch {
        /* */
      }
    }
    return first?.polipay_account || first?.clabe || first?.clabe_account || null;
  }

  /**
   * Busca la entidad actual (balance_object) dentro del árbol de fathers.
   */
  private findEntityInTree(current: any, fathers: any[]): any | null {
    if (!current || !Array.isArray(fathers)) return null;
    const entityType = (current?.entity_type ?? '').toString().trim();

    // Comparar con String() para evitar fallos number vs string (ej. 3 === "3")
    const eq = (a: any, b: any) => a != null && b != null && String(a) === String(b);

    // Para Cta_Padre: clabe es el identificador único confiable.
    // polipay_account y sufijo pueden ser compartidos entre padres del mismo usuario.
    const matchFather = (a: any) => {
      // 1. Match por clabe (único por cuenta padre — ej. 646... vs 684...)
      if (current?.clabe && eq(a?.clabe, current?.clabe)) return true;
      // 2. Match por id_entity_name + provider (combinación única entre padres del mismo usuario)
      if (current?.id_entity_name && a?.id_entity_name &&
        (a?.id_entity_name ?? '').toString().trim() === (current?.id_entity_name ?? '').toString().trim() &&
        (a?.provider ?? '').toString().trim() === (current?.provider ?? '').toString().trim()) return true;
      // 3. Match por polipay_account solo si es un valor distinto al sufijo y es único
      if (current?.polipay_account && current.polipay_account !== current.sufijo &&
        eq(a?.polipay_account, current?.polipay_account)) return true;
      return false;
    };
    const matchCompany = (a: any) =>
      eq(a?.id_company, current?.id_company) ||
      eq(a?.clabe, current?.clabe) ||
      eq(a?.polipay_account, current?.polipay_account);
    const matchBU = (a: any) =>
      eq(a?.id_business_unit, current?.id_business_unit) ||
      eq(a?.clabe, current?.clabe) ||
      eq(a?.polipay_account, current?.polipay_account) ||
      eq(a?.clabe_account, current?.clabe_account);
    const matchAccount = (a: any) =>
      eq(a?.id_account, current?.id_account) ||
      eq(a?.clabe, current?.clabe) ||
      eq(a?.polipay_account, current?.polipay_account) ||
      eq(a?.clabe_account, current?.clabe_account);

    for (const p of fathers) {
      if (entityType === 'Cta_Padre' && matchFather(p)) return p;
      for (const c of p?.companies ?? []) {
        if (entityType === 'Empresa' && matchCompany(c)) return c;
        for (const bu of c?.business_units ?? []) {
          if ((entityType === 'Unidad de Negocio' || entityType === 'Unidad  de Negocio') && matchBU(bu)) return bu;
          for (const acc of bu?.accounts ?? []) {
            if (entityType === 'Cuenta' && matchAccount(acc)) return acc;
          }
        }
      }
    }
    return null;
  }

  /**
   * Llama a api/father_account/balances/{search}/ con query params opcionales
   * (id_company, id_business_unit, id_account) para obtener saldos frescos
   * hasta el nivel de la entidad actualmente seleccionada.
   */
  private refreshAllBalancesFromAPI(accountIdFallback: string): void {
    const userPolipay = this.getUserPolipayForApi();
    if (!userPolipay) return;

    this.fatherAccountCache.clear();

    // Obtener la entidad actual para construir los query params
    const balanceObj = this.safeGetBalanceObjectFromStorage();
    const current = Array.isArray(balanceObj) && balanceObj[0] ? balanceObj[0] : balanceObj;
    const entityType = (current?.entity_type ?? '').toString().trim();

    // R28 (products_spei_sin_cuenta_padre, Q9): sin gestor SPEI de Cta_Padre la
    // sesión no tiene cuenta padre que refrescar, y pedir `/api/father_account/`
    // sería una consulta de nivel superior. Mientras la entidad activa no sea una
    // Cta_Padre, se refresca SOLO por el endpoint de su propio nivel, con el mismo
    // emparejamiento estricto de identidad de siempre.
    if (entityType.replace(/\s+/g, '_') !== 'Cta_Padre' && this.sessionLacksSpeiFatherGestor()) {
      if (current) this.refreshThroughOwnLevel(current, this.identityApplierFor(current));
      return;
    }

    // Construir query params según el nivel de la entidad
    let params = new HttpParams();
    if (current?.id_company) {
      params = params.set('id_company', current.id_company);
    }
    if ((entityType === 'Unidad de Negocio' || entityType === 'Unidad  de Negocio' || entityType === 'Cuenta') && current?.id_business_unit) {
      params = params.set('id_business_unit', current.id_business_unit);
    }
    if (entityType === 'Cuenta' && current?.id_account) {
      params = params.set('id_account', current.id_account);
    }

    const token = sessionStorage.getItem('access');
    const url = `${environment.apiUrl}/api/father_account/balances/${userPolipay}/`;
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    this.http.get<any>(url, { headers, params }).pipe(
      timeout(this.BALANCE_HTTP_TIMEOUT_MS),
      catchError(() => of(null))
    ).subscribe((response) => {
      if (!response) return;

      const fathers: any[] = Array.isArray(response) ? response : [response];

      // Solo actualizar father_accounts_list si NO es una consulta filtrada (drill-down).
      // Cuando hay id_company/id_business_unit, la API devuelve un subconjunto del arbol
      // y sobrescribirlo elimina las demas cuentas padre del carrusel.
      const isFilteredRequest = params.keys().length > 0;
      if (!isFilteredRequest) {
        try {
          // Preservar providers de la lista existente
          const existingEnc = sessionStorage.getItem('father_accounts_list');
          let providerMap: Record<string, string> = {};
          if (existingEnc) {
            try {
              const existing = JSON.parse(this.encryptionService.decrypt(existingEnc));
              if (Array.isArray(existing)) {
                existing.forEach((f: any) => {
                  const key = f?.polipay_account || f?.sufijo;
                  if (key && f?.provider) providerMap[key] = f.provider;
                });
              }
            } catch {}
          }
          // Re-aplicar providers antes de guardar
          fathers.forEach((f: any) => {
            const key = f?.polipay_account || f?.sufijo;
            if (key && providerMap[key] && !f.provider) {
              f.provider = providerMap[key];
            }
          });

          this.browserStorage.setSessionStorageItem(
            'father_accounts_list',
            this.encryptionService.encrypt(JSON.stringify(fathers))
          );
        } catch {}
      }

      // Verificar que el usuario no haya navegado a otra entidad durante la request.
      const currentNow = this.safeGetBalanceObjectFromStorage();
      const firstNow = Array.isArray(currentNow) && currentNow[0] ? currentNow[0] : currentNow;
      const nowEntityType = (firstNow?.entity_type ?? '').toString().trim();
      const beforeEntityType = entityType;

      if (beforeEntityType && nowEntityType && beforeEntityType !== nowEntityType) {
        return; // Cambió de nivel de entidad
      }

      // Identidad canónica única de Cta_Padre = (provider, id_entity_name).
      // clabe no existe a nivel Cta_Padre; polipay_account y sufijo pueden colisionar.
      const buildFatherKey = (e: any) =>
        `${(e?.provider ?? '').toString().trim()}::${(e?.id_entity_name ?? '').toString().trim()}`;

      if (beforeEntityType === 'Cta_Padre') {
        if (buildFatherKey(current) !== buildFatherKey(firstNow)) {
          return; // El usuario cambió a otra Cta_Padre — descartar respuesta stale
        }
      } else {
        const nowId = firstNow?.id_company ?? firstNow?.id_business_unit ?? firstNow?.id_account ?? firstNow?.clabe ?? '';
        const beforeId = current?.id_company ?? current?.id_business_unit ?? current?.id_account ?? current?.clabe ?? '';
        if (beforeId && nowId && String(beforeId) !== String(nowId)) {
          return;
        }
      }

      const applyIdentity = this.identityApplierFor(current);

      // Buscar la entidad actual en el árbol fresco
      const freshEntity = current ? this.findEntityInTree(current, fathers) : null;
      if (freshEntity) {
        // Re-validar identidad: findEntityInTree puede devolver el padre equivocado
        // si current tiene campos faltantes. Sin esta verificación, el merge pisa la selección.
        if (beforeEntityType === 'Cta_Padre' && buildFatherKey(current) !== buildFatherKey(freshEntity)) {
          return;
        }
        const cachedProvider = (current?.provider ?? '').toString().trim();
        const hasProvider = (freshEntity?.provider ?? '').toString().trim();
        const toStore = cachedProvider && !hasProvider
          ? { ...freshEntity, provider: cachedProvider }
          : freshEntity;
        // Toma saldos y datos auxiliares del árbol fresco, pero IDENTIDAD del snapshot.
        const merged = applyIdentity({ ...current, ...toStore });
        this.setBalanceInfo(merged);
      } else if (current) {
        // Fallback para entidades que no se encontraron en el árbol de Cta_Padre:
        // Cuenta/Empresa/UN por el endpoint de su nivel (`refreshThroughOwnLevel`).
        if (!this.refreshThroughOwnLevel(current, applyIdentity)) {
          // Fallback final Cta_Padre: requerir provider + id_entity_name (identidad canónica).
          // Sin ambos, NO aplicar — mejor saldo desactualizado que saldo de otra cuenta.
          const currentName = (current?.id_entity_name ?? '').toString().trim();
          const currentProvider = (current?.provider ?? '').toString().trim();
          if (!currentName || !currentProvider) return;
          const matchedFather = fathers.find((f: any) =>
            (f?.id_entity_name ?? '').toString().trim() === currentName &&
            (f?.provider ?? '').toString().trim() === currentProvider
          );
          if (matchedFather) {
            const merged = applyIdentity({ ...current, ...matchedFather });
            this.setBalanceInfo(merged);
          }
        }
      }
    });
  }

  /**
   * ¿La sesión carece de gestor SPEI de Cta_Padre? (R28). Solo responde `true` con
   * EVIDENCIA: un `user` legible cuyos gestores no tienen ninguno. Sin `user`
   * legible (p. ej. el flujo `_identify`) se conserva el comportamiento de antes.
   */
  private sessionLacksSpeiFatherGestor(): boolean {
    const stored = sessionStorage.getItem('user');
    if (!stored) return false;
    try {
      const parsed = JSON.parse(this.encryptionService.decrypt(stored));
      const user = Array.isArray(parsed) ? parsed[0] : parsed;
      if (!user || typeof user !== 'object') return false;
      return !hasSpeiFatherGestor(Array.isArray(user.gestores) ? user.gestores : []);
    } catch {
      return false;
    }
  }

  /**
   * Snapshot inmutable de IDENTIDAD de la entidad seleccionada por el usuario al
   * momento del disparo. Toda fusión posterior reescribirá estos campos al final
   * para garantizar que un `freshEntity` con identidad distinta (matching ambiguo,
   * API que regresa el padre en lugar del hijo, etc.) NUNCA pueda filtrar nombre
   * ni cuenta de otra entidad sobre la selección actual. Únicos campos no-identidad
   * (saldos, contadores, hijos) pueden cambiar libremente.
   */
  private identityApplierFor(current: any): (obj: any) => any {
    const identitySnapshot = current ? {
      entity_type: current.entity_type,
      id_entity_name: current.id_entity_name,
      provider: current.provider,
      gestor_role_id: current.gestor_role_id,
      polipay_account: current.polipay_account,
      clabe: current.clabe,
      clabe_account: current.clabe_account,
      id_company: current.id_company,
      id_business_unit: current.id_business_unit,
      id_account: current.id_account,
      sufijo: current.sufijo,
    } : null;

    // Aplica el snapshot a cualquier merge antes de persistirlo. Sólo sobrescribe
    // campos definidos (undefined en snapshot deja pasar el valor del merge).
    return (obj: any): any => {
      if (!obj || !identitySnapshot) return obj;
      const out = { ...obj };
      for (const key of Object.keys(identitySnapshot) as (keyof typeof identitySnapshot)[]) {
        const v = identitySnapshot[key];
        if (v !== undefined) (out as any)[key] = v;
      }
      return out;
    };
  }

  /**
   * Refresca una Empresa/UN/Cuenta por el endpoint de SU nivel, con emparejamiento
   * estricto de identidad (nunca `items[0]`). Devuelve `false` si la entidad no se
   * puede refrescar así (Cta_Padre, sin tipo o sin identificador): el llamador decide.
   */
  private refreshThroughOwnLevel(current: any, applyIdentity: (obj: any) => any): boolean {
    const currentEntityType = (current?.entity_type ?? '').toString().trim();
    const directPolipay = current?.polipay_account || current?.clabe || current?.clabe_account;

    // Si es Cuenta/Empresa/UN sin params de cascada, llamar directo a su endpoint
    if (!(directPolipay && currentEntityType && currentEntityType !== 'Cta_Padre')) return false;
    this.getAccountsByEntityType(currentEntityType, directPolipay).subscribe(items => {
      if (items?.length) {
        // La API puede devolver multiples entidades — matchear la correcta por ID único
        const currentId = current?.id_company ?? current?.id_business_unit ?? current?.id_account;
        const currentClabe = (current?.clabe ?? '').toString().trim();
        const currentName = (current?.id_entity_name ?? '').toString().trim();
        const fresh = items.find((item: any) => {
          const itemId = item?.id_company ?? item?.id_business_unit ?? item?.id_account;
          if (currentId && itemId && String(currentId) === String(itemId)) return true;
          if (currentClabe && String(item?.clabe ?? '') === currentClabe) return true;
          if (currentName && (item?.id_entity_name ?? '').toString().trim() === currentName) return true;
          return false;
        });
        if (!fresh) return; // Sin match exacto, NO aplicar items[0] — mejor saldo viejo que saldo equivocado

        // Guard estricto extra: si la API trae un item con id_entity_name distinto
        // al del snapshot, abortar. Cubre casos donde la API regresa un wrapper
        // Cta_Padre o un item con datos cruzados que pase los otros filtros.
        const freshName = (fresh?.id_entity_name ?? '').toString().trim();
        if (currentName && freshName && currentName !== freshName) {
          return;
        }
        // Guard estricto: requerimos coincidencia de ID antes de aplicar
        const freshId = fresh?.id_company ?? fresh?.id_business_unit ?? fresh?.id_account;
        if (currentId && freshId && String(currentId) !== String(freshId)) {
          return;
        }

        // Revalidar storage: si el usuario cambió de entidad mientras esta fallback
        // estaba en vuelo, descartar para no escribir saldos sobre la nueva selección.
        const storedNow = this.safeGetBalanceObjectFromStorage();
        const storedNowFirst = Array.isArray(storedNow) && storedNow[0] ? storedNow[0] : storedNow;
        if (storedNowFirst && !this.isSameEntityIdentity(storedNowFirst, current)) {
          return;
        }

        // Toma saldos del API, IDENTIDAD del snapshot.
        const merged = applyIdentity({ ...current, ...fresh });
        this.setBalanceInfo(merged);
      }
    });
    return true;
  }

  /**
   * Refresca el saldo desde api/father_account/balances.
   * Obtiene el árbol completo, guarda en father_accounts_list (sessionStorage)
   * y actualiza balance_object con la entidad actual.
   * Usar tras transferencias, al cambiar pantalla, recarga o volver al tab.
   */
  refreshBalance(): void {
    if (!this.isBrowser || typeof sessionStorage === 'undefined') return;
    if (!sessionStorage.getItem('access')) return;

    const balanceObj = this.safeGetBalanceObjectFromStorage();
    const first = Array.isArray(balanceObj) && balanceObj[0] ? balanceObj[0] : balanceObj;
    let accountId = first?.polipay_account || first?.clabe || first?.clabe_account;
    if (!accountId) {
      const parsed = this.getUserPolipayForApi();
      accountId = parsed || '';
    }
    if (!accountId) return;

    this.refreshAllBalancesFromAPI(accountId);
  }

  /**
  * Obtiene el saldo actual como valor síncrono
  * @returns El saldo actual
  */
  getBalanceValue(): number {
    return this.balanceSubject.value;
  }

  /**
  * Resta un monto del saldo disponible
  */
  deductAmount(amount: number): boolean {
    const currentBalance = this.getBalanceValue();
    if (amount <= 0) return false;
    if (amount > currentBalance) {
      return false;
    }

    const newBalance = currentBalance - amount;
    this.setBalance(newBalance);
    return true;
  }

  /**
  * Agrega un monto al saldo disponible (útil para reversiones)
  */
  addAmount(amount: number): void {
    if (amount <= 0) return;

    const currentBalance = this.getBalanceValue();
    const newBalance = currentBalance + amount;
    this.setBalance(newBalance);
  }

  /**
  * Inicializa el saldo por defecto si no existe en localStorage
  */
  initializeDefaultBalance(): void {
    const currentBalance: number = this.safeGetBalanceFromStorage();

    if (currentBalance === 0.00) {
      this.setBalance(0.00);
    }
    // si quieres mantener el valor encontrado: this.setBalance(currentBalance)
  }

  /**
   * setBalanceInfo: actualiza los subjects y guarda en storage el objeto completo.
   * - companyInfo puede ser un objeto o un array; lo normalizamos a array.
   */
  setBalanceInfo(companyInfo: any): void {
    if (!companyInfo) return;

    const companyArray: any[] = Array.isArray(companyInfo) ? companyInfo : [companyInfo];
    const first = companyArray[0];

    // Preservar campos de saldo: buscar en storage (misma entidad) o father_accounts_list (para Cta_Padre)
    const BALANCE_FIELDS = ['balance', 'balance_account', 'balance_company', 'balance_father_account', 'global_balance_unit'];
    const hasMissingBalances = first && BALANCE_FIELDS.every(f => first[f] == null);

    // 1. Merge desde storage si es la misma entidad
    const cached = this.safeGetBalanceObjectFromStorage();
    const cachedFirst = Array.isArray(cached) && cached[0] ? cached[0] : cached;
    if (cachedFirst && first &&
      (cachedFirst.id_entity_name === first.id_entity_name || cachedFirst.clabe === first.clabe)) {
      for (const field of BALANCE_FIELDS) {
        if (first[field] == null && cachedFirst[field] != null) {
          first[field] = cachedFirst[field];
        }
      }
    }

    // 2. Si aún faltan saldos, buscar en father_accounts_list (fuente con saldos frescos de la API)
    if (first && BALANCE_FIELDS.every(f => first[f] == null)) {
      try {
        const encFathers = sessionStorage.getItem('father_accounts_list');
        if (encFathers) {
          const fathers = JSON.parse(this.encryptionService.decrypt(encFathers));
          if (Array.isArray(fathers)) {
            const entityName = (first.id_entity_name ?? '').toString().trim();
            const entityClabe = (first.clabe ?? '').toString().trim();
            const match = fathers.find((f: any) =>
              (entityClabe && (f?.clabe ?? '').toString().trim() === entityClabe) ||
              (entityName && (f?.id_entity_name ?? '').toString().trim() === entityName)
            );
            if (match) {
              for (const field of BALANCE_FIELDS) {
                if (first[field] == null && match[field] != null) {
                  first[field] = match[field];
                }
              }
            }
          }
        }
      } catch {}
    }

    // Persistir ANTES de emitir: varios suscriptores reaccionan a balanceInfo$ y leen
    // balance_object de sessionStorage para recargar sus datos (dashboard, transacciones,
    // etc.). Si emitiéramos primero, leerían la entidad ANTERIOR (aún no escrita). Escribir
    // antes garantiza que todo el que reaccione al aviso ya vea la entidad nueva.
    if (this.isBrowser && typeof sessionStorage !== 'undefined') {
      try {
        const encryptedCompanyInfo = this.encryptionService.encrypt(JSON.stringify(companyArray));
        this.browserStorage.setSessionStorageItem(this.BALANCE_OBJECT_KEY, encryptedCompanyInfo);
      } catch (err) {
      }
    }

    this.balanceObjectSubject.next(companyArray);

    let newBalance = 0;
    if (first) {
      newBalance = this.determineBalance(first);
    }

    if (!isNaN(newBalance)) {
      this.setBalance(newBalance);
    }

    if (first?.clabe) {
      this.clabeSubject.next(first.clabe);
    }
  }
}
